import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './CognitiveAgeCard.css';
import { getAppParams } from '../../apiService/timespam';
import brainIcon from '../../assets/img/brainlogo.png';
import ageStagesImg from '../../assets/img/evolution-residence-man-from-birth-old-age-stages.png';
import { APIV4_BASE_URL } from '../../apiService/apiClient';

const DEFAULT_TOTAL_GAMES = 18;
const MIN_GAMES_TO_UNLOCK = 10;

// The age-stages image is divided into 6 equal-width brackets (matching its
// own baked-in axis labels: 7-9, 12-14, 18-25, 30-35, 40-55, 60-70). Each
// entry is the inclusive upper bound of that bracket's real-world age range.
const AGE_BRACKETS = [9, 14, 25, 35, 55, Infinity];

// cogAge.cognitiveage is a formatted string like "34y 2m 10d" -- pull the
// leading year count out of it.
const parseYearsFromAgeString = (str) => {
  const match = /(\d+)\s*y/.exec(str || '');
  return match ? parseInt(match[1], 10) : null;
};

const bracketIndexForAge = (years) => {
  for (let i = 0; i < AGE_BRACKETS.length; i++) {
    if (years <= AGE_BRACKETS[i]) return i;
  }
  return AGE_BRACKETS.length - 1;
};

const InfoIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <line x1="12" y1="11" x2="12" y2="16.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="7.5" r="1.15" fill="currentColor" />
  </svg>
);

/**
 * Self-contained Cognitive Age card: fetches its own data and renders the
 * card + info modal. Drop it into any page with zero prop wiring -- it reads
 * user/session identifiers straight from the current URL (getAppParams()).
 */
const CognitiveAgeCard = ({ onPlayNow }) => {
  const { t } = useTranslation();
  const [cogAge, setCogAge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    const fetchCogAge = async () => {
      try {
        const { uid, goqiiUserId, nonce, apiKey, signature, goqiiAccessToken, appVersion, appType } = getAppParams();
        const body = new URLSearchParams({
          goqiiUserId, nonce, apiKey, signature, goqiiAccessToken, appVersion, appType,
          userId: uid,
        }).toString();
        const res = await fetch(`${APIV4_BASE_URL}/games/cognitive_age`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body,
        });
        const json = await res.json();
        if (json.code === 200) setCogAge(json.data || {});
      } catch (e) {
        console.error('cognitive_age fetch failed', e);
      } finally {
        setLoading(false);
      }
    };
    fetchCogAge();
  }, []);

  const handlePlayNow = () => {
    setShowInfo(false);
    if (onPlayNow) onPlayNow();
  };

  if (loading) {
    return (
      <div className="cac-card cac-skeleton">
        <div className="cac-skeleton-line cac-skeleton-title" />
        <div className="cac-skeleton-block" />
      </div>
    );
  }

  // API failed entirely -- fail quiet, rest of the page still works fine without this card
  if (!cogAge) return null;

  const totalGames = cogAge.totalGames || DEFAULT_TOTAL_GAMES;
  const gamesPlayed = cogAge.gamesPlayed || 0;
  const unlocked = cogAge.showcard !== 'N' && !!cogAge.cognitiveage;
  const gamesLeft = Math.max(MIN_GAMES_TO_UNLOCK - gamesPlayed, 0);

  const cognitiveAgeYears = unlocked ? parseYearsFromAgeString(cogAge.cognitiveage) : null;
  const bracketIndex = cognitiveAgeYears != null ? bracketIndexForAge(cognitiveAgeYears) : AGE_BRACKETS.length - 1;
  const boundaryPct = ((bracketIndex + 1) / AGE_BRACKETS.length) * 100;

  return (
    <>
      <div className="cac-card">
        <div className="cac-header">
          <img src={brainIcon} alt="" className="cac-header-icon" />
          <span className="cac-title">{t('games.cognitiveAge.title')}</span>
          <button
            type="button"
            className="cac-info-btn"
            aria-label={t('games.cognitiveAge.infoAlt')}
            onClick={() => setShowInfo(true)}
          >
            <InfoIcon />
          </button>
        </div>

        {unlocked ? (
          <div className="cac-stage">
            <div className="cac-stage-img-wrap">
              <img src={ageStagesImg} alt="" className="cac-stage-img cac-stage-img--mono" />
              <img
                src={ageStagesImg}
                alt=""
                className="cac-stage-img cac-stage-img--color"
                style={{ clipPath: `inset(0 ${100 - boundaryPct}% 0 0)` }}
              />
            </div>
            <div className="cac-track">
              <div className="cac-track-fill" style={{ width: `${boundaryPct}%` }} />
              <div className="cac-divider" style={{ left: `${boundaryPct}%` }} />
              <div className="cac-pin" style={{ left: `${boundaryPct}%` }}>
                <span className="cac-pin-label">{cogAge.cognitiveage}</span>
              </div>
            </div>
            <div className="cac-details">
              <div className="cac-detail-item">
                <span className="cac-detail-label">{t('games.cognitiveAge.actualAge')}</span>
                <span className="cac-detail-value">{cogAge.actualage}</span>
              </div>
              <div className="cac-detail-item">
                <span className="cac-detail-label">{t('games.cognitiveAge.cognitiveAgeLabel')}</span>
                <span className="cac-detail-value">{cogAge.cognitiveage}</span>
              </div>
              {cogAge.text && (
                <span className={`cac-diff-tag cac-${cogAge.difference}`}>{cogAge.text}</span>
              )}
            </div>
          </div>
        ) : (
          <div className="cac-locked">
            <img src={ageStagesImg} className="cac-img cac-img-mono" alt="" />
            <p className="cac-locked-text">
              {gamesLeft > 0
                ? t('games.cognitiveAge.lockedMore', { count: gamesLeft })
                : t('games.cognitiveAge.lockedCalculating')}
            </p>
            <div className="cac-progress-bg">
              <div
                className="cac-progress-fill"
                style={{ width: `${Math.min(100, (gamesPlayed / MIN_GAMES_TO_UNLOCK) * 100)}%` }}
              />
            </div>
            <span className="cac-progress-label">
              {t('games.cognitiveAge.lockedProgress', { played: gamesPlayed, total: MIN_GAMES_TO_UNLOCK })}
            </span>
          </div>
        )}
      </div>

      {showInfo && (
        <div className="cac-modal-overlay" role="dialog" aria-modal="true" onClick={() => setShowInfo(false)}>
          <div className="cac-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="cac-modal-close" aria-label="Close" onClick={() => setShowInfo(false)}>×</button>

            <img src={brainIcon} alt="" className="cac-modal-icon" />
            <h3 className="cac-modal-title">{t('games.cognitiveAge.modalTitle')}</h3>

            <p className="cac-modal-text">{t('games.cognitiveAge.modalText1')}</p>
            <p className="cac-modal-text">{t('games.cognitiveAge.modalText2')}</p>
            <p className="cac-modal-text">
              {t('games.cognitiveAge.modalText3', { minGames: MIN_GAMES_TO_UNLOCK, totalGames })}
            </p>
            <p className="cac-modal-text">{t('games.cognitiveAge.modalText4')}</p>

            <button type="button" className="cac-modal-cta" onClick={handlePlayNow}>
              {t('games.cognitiveAge.playNow')}
            </button>

            <p className="cac-modal-disclaimer">{t('games.cognitiveAge.disclaimer')}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default CognitiveAgeCard;
